
import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../AppContext';
import { collection, addDoc, serverTimestamp, query, onSnapshot, orderBy, where } from 'firebase/firestore';
import CheerAnimation from './CheerAnimation';
import { Heart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const SideBySideViewer = ({ gameComponent: GameComponent, challenger, challenged, matchId }) => {
    const { t } = useTranslation();
    const { db } = useAppContext();
    const [activeView, setActiveView] = useState('challenger');
    const [challengerCheers, setChallengerCheers] = useState([]);
    const [challengedCheers, setChallengedCheers] = useState([]);

    useEffect(() => {
        if (!db || !matchId) return;

        const cheersQuery = (playerId) => query(
            collection(db, `challenges/${matchId}/cheers`),
            where('playerId', '==', playerId),
            orderBy('timestamp')
        );

        const unsubChallenger = onSnapshot(cheersQuery(challenger.id), (snapshot) => {
            setChallengerCheers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const unsubChallenged = onSnapshot(cheersQuery(challenged.id), (snapshot) => {
            setChallengedCheers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        return () => {
            unsubChallenger();
            unsubChallenged();
        };
    }, [db, matchId, challenger.id, challenged.id]);

    const handleCheer = async (playerId) => {
        if (!db || !matchId) return;
        await addDoc(collection(db, `challenges/${matchId}/cheers`), {
            playerId,
            timestamp: serverTimestamp(),
        });
    };

    const PlayerHeader = ({ player, isActive, onClick, onCheer, color }) => (
        <div 
            className={`p-2 text-center text-white cursor-pointer md:cursor-default border-b-4 ${isActive ? color : 'border-transparent'}`}
            onClick={onClick}
        >
            <div className="flex items-center justify-center">
                <h2 className="text-lg font-bold">{player.name}</h2>
                <button onClick={(e) => { e.stopPropagation(); onCheer(); }} className="ml-4 text-red-500 hover:text-red-400 transition-transform transform hover:scale-125">
                    <Heart size={20} />
                </button>
            </div>
            <p className="text-xs text-gray-400">{t('sideBySideViewer.souls')}: {player.souls}</p>
        </div>
    );

    const PlayerGameView = ({ player, gameProps, cheers, onCheer, borderColor }) => (
        <div className={`flex-1 flex flex-col border-2 ${borderColor} rounded-lg overflow-auto relative`}>
            <div className="bg-gray-800 p-2 text-center text-white hidden md:block">
                <div className="flex items-center justify-center">
                    <h2 className="text-xl font-bold">{player.name}</h2>
                    <button onClick={onCheer} className="ml-4 text-red-500 hover:text-red-400 transition-transform transform hover:scale-125">
                        <Heart size={24} />
                    </button>
                </div>
                <p className="text-sm text-gray-400">{t('sideBySideViewer.souls')}: {player.souls}</p>
            </div>
            <div className="flex-grow">
                <GameComponent {...gameProps} isSpectatorMode={true} />
            </div>
            <CheerAnimation cheers={cheers} />
        </div>
    );

    const mobileViewVariants = {
        enter: (direction) => ({
            x: direction > 0 ? '100%' : '-100%',
            opacity: 0
        }),
        center: {
            x: 0,
            opacity: 1
        },
        exit: (direction) => ({
            x: direction < 0 ? '100%' : '-100%',
            opacity: 0
        })
    };

    return (
        <div className="w-full h-full flex flex-col bg-black">
            <div className="md:hidden grid grid-cols-2 bg-gray-800">
                <PlayerHeader player={challenger} isActive={activeView === 'challenger'} onClick={() => setActiveView('challenger')} onCheer={() => handleCheer(challenger.id)} color="border-cyan-500" />
                <PlayerHeader player={challenged} isActive={activeView === 'challenged'} onClick={() => setActiveView('challenged')} onCheer={() => handleCheer(challenged.id)} color="border-red-500" />
            </div>

            <div className="hidden md:flex flex-grow gap-4 p-4">
                <PlayerGameView player={challenger} gameProps={challenger.gameProps} cheers={challengerCheers} onCheer={() => handleCheer(challenger.id)} borderColor="border-cyan-500" />
                <PlayerGameView player={challenged} gameProps={challenged.gameProps} cheers={challengedCheers} onCheer={() => handleCheer(challenged.id)} borderColor="border-red-500" />
            </div>

            <div className="md:hidden flex-grow relative overflow-hidden">
                <AnimatePresence initial={false} custom={activeView === 'challenger' ? 1 : -1}>
                    <motion.div
                        key={activeView}
                        custom={activeView === 'challenger' ? 1 : -1}
                        variants={mobileViewVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ x: { type: "spring", stiffness: 300, damping: 30 }, opacity: { duration: 0.2 } }}
                        className="w-full h-full absolute"
                    >
                        {activeView === 'challenger' ? (
                            <div className="w-full h-full border-x-2 border-b-2 border-cyan-500 rounded-b-lg overflow-auto">
                                <GameComponent {...challenger.gameProps} isSpectatorMode={true} />
                                <CheerAnimation cheers={challengerCheers} />
                            </div>
                        ) : (
                            <div className="w-full h-full border-x-2 border-b-2 border-red-500 rounded-b-lg overflow-auto">
                                <GameComponent {...challenged.gameProps} isSpectatorMode={true} />
                                <CheerAnimation cheers={challengedCheers} />
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
};

export default SideBySideViewer;
